import { el, jsonToVDOM, VNode, xmlToJson } from "@dhx/ts-common/dom";
import { isEmptyObj } from "@dhx/ts-common/core";
import { ICustomShapeConfig } from "@dhx/ts-diagram";
import { BaseShape } from "./BaseShape";
import { getCircleTpl } from "../templates/templates";
import { getMeta } from "../../helpers/baseMetaInfo";
import locale from "../../locales/en";

export class CustomShape extends BaseShape {
	public config: ICustomShapeConfig;
	public shapes;
	private _properties;

	constructor(config: ICustomShapeConfig, parameters?) {
		super(config, parameters);
		this.shapes = parameters.shapes;
		this._properties = parameters.properties;
	}
	public getMetaInfo(): { [key: string]: string }[] {
		return this._getMetaInfoStructure(this.config);
	}
	public render(): VNode {
		const { id, angle, width, height, $group, $selected } = this.config;
		const zIndex = $group || $selected ? ($selected ? 2 : 1) : 0;
		const coords = this.getCoords(this.config);

		const circle = [];
		if (Array.isArray(this.config.dir)) {
			this.config.dir.forEach((_dir, index) => {
				circle.push(getCircleTpl(this.config, index));
			});
		} else {
			circle.push(getCircleTpl(this.config));
		}

		return el(
			"div",
			{
				_key: id,
				class: "dhx_diagram_item dhx_diagram_shape dhx_diagram_flow_item " + this.getCss(),
				dhx_id: id,
				style: {
					transform: `rotate(${angle || 0}deg)`,
					zIndex,
					position: "absolute",
					height,
					width,
					top: coords.y,
					left: coords.x,
				},
			},
			[this._getCustomContent(), ...circle]
		);
	}
	protected setDefaults(config: ICustomShapeConfig, defaults?: ICustomShapeConfig): ICustomShapeConfig {
		Object.keys(defaults).forEach(property => {
			config[property] = config[property] || defaults[property];
		});

		const widthDefaut = defaults.width ? parseFloat(defaults.width as any) : 140;
		const heightDefault = defaults.height ? parseFloat(defaults.height as any) : 90;

		config.width = config.width || widthDefaut;
		config.height = config.height || heightDefault;

		config.x = config.x || 0;
		config.y = config.y || 0;

		config.fixed = config.fixed || false;

		return config;
	}
	private _getMetaInfoStructure(config: ICustomShapeConfig): { [key: string]: string }[] {
		return !isEmptyObj(this._properties)
			? getMeta(this._properties)
			: this._getBaseMetaInfoStructure(config);
	}
	private _getBaseMetaInfoStructure(config: ICustomShapeConfig): { [key: string]: string }[] {
		const {
			text,
			title,
			img,
			fill,
			stroke,
			strokeWidth,
			textAlign,
			lineHeight,
			fontStyle,
			textVerticalAlign,
			fontSize,
		} = config;

		let metaInfo = [];

		if (config.$expandColor) {
			metaInfo = getMeta([
				{ type: "grid", label: locale.gridStep },
				{ type: "position", label: locale.position },
				{ type: "size", label: locale.size },
			]);
		} else {
			metaInfo = getMeta([
				{ type: "grid", label: locale.gridStep },
				{ type: "arrange", label: locale.arrange },
			]);
		}
		if (fill) {
			metaInfo.push(getMeta([{ type: "fill", label: locale.fill }])[0]);
		}
		if (title) {
			metaInfo.push(getMeta([{ type: "title", label: locale.title }])[0]);
		}
		if (text) {
			metaInfo.push(getMeta([{ type: "text", label: locale.text }])[0]);
		}
		if (stroke && strokeWidth) {
			metaInfo.push(getMeta([{ type: "strokeProps", label: locale.stroke }])[0]);
		}
		if (textAlign && lineHeight && fontStyle && textVerticalAlign && fontSize) {
			metaInfo.push(getMeta([{ type: "textProps", label: locale.textProps }])[0]);
		}
		if (img) {
			metaInfo.push(getMeta([{ type: "img", label: locale.image }])[0]);
		}

		return metaInfo;
	}
	private _getCustomContent(): VNode {
		const { type, width, height } = this.config;
		const shape = this.shapes[type];

		if (typeof shape !== "function") {
			return;
		}
		return el(
			"div",
			{
				style: {
					width,
					height,
				},
			},
			this._getShapeFromFunction(shape)
		);
	}
	private _normalizeConfigText(text: string | string[]): string | string[] {
		if (Array.isArray(text)) {
			text.forEach(el => this._normalizeConfigText(el));
		} else if (typeof text === "string") {
			if (text.includes(`&`)) text = text.replace(/&/g, "&amp;");
			if (text.includes(`<`)) text = text.replace(/</g, "&lt;");
			if (text.includes(`>`)) text = text.replace(/>/g, "&gt;");
			if (text.includes(`'`)) text = text.replace(/'/g, "&apos;");
			if (text.includes(`"`)) text = text.replace(/"/g, "&quot;");
		}
		return text;
	}
	private _getShapeFromFunction(func: (config: ICustomShapeConfig) => string): VNode[] {
		const preparedValues = {};
		Object.entries(this.config).forEach(([key, value]) => {
			if (
				(key !== "id" &&
					key !== "type" &&
					key !== "preview" &&
					!key.startsWith("$") &&
					typeof value === "string") ||
				Array.isArray(value)
			) {
				preparedValues[key] = this._normalizeConfigText(value);
			}
		});

		const shape = new DOMParser().parseFromString(
			func(Object.assign({}, this.config, preparedValues)),
			"text/xml"
		);
		return [jsonToVDOM(xmlToJson(shape))];
	}
}
